/**
 * SubNav — overlay popover anchored beside the left rail. Appears when
 * the user clicks a top-level nav item that has `children` and hides
 * when the user clicks outside, presses Escape, or picks a child.
 *
 * Visibility is controlled by the parent layout (MainLayout), which owns
 * the `openedParentId` state and also hands it to Sidebar so the parent
 * icon stays highlighted while the overlay is open.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navItems } from './Sidebar';

export default function SubNav({ openedParentId, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);

  const parent = openedParentId
    ? navItems.find((i) => i.id === openedParentId) || null
    : null;

  // Outside-click + Escape to dismiss.
  useEffect(() => {
    if (!parent) return undefined;
    const handleDown = (e) => {
      if (!ref.current) return;
      // Clicks inside the overlay OR anywhere on the left rail must not
      // close the menu — the rail click is handled by Sidebar itself
      // (toggle behaviour).
      if (ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.nav-sidebar')) return;
      onClose?.();
    };
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [parent, onClose]);

  if (!parent || !parent.children || parent.children.length === 0) return null;

  const isActive = (child) =>
    location.pathname === child.path || location.pathname.startsWith(`${child.path}/`);

  return (
    <aside ref={ref} className="sub-nav" role="menu" aria-label={`${parent.label} sections`}>
      <h2 className="sub-nav-title">{parent.label}</h2>
      <div className="sub-nav-menu">
        {parent.children.map((child) => (
          <button
            key={child.id}
            type="button"
            role="menuitem"
            className={`sub-nav-item ${isActive(child) ? 'active' : ''}`}
            onClick={() => {
              navigate(child.path);
              onClose?.();
            }}
          >
            {child.label}
          </button>
        ))}
      </div>
    </aside>
  );
}


